import type { ConnectorTypeRead, UploadModelRead, UploadRead } from "../connectorsV2/connectorsV2.types";
import type { StepsProgressStatus } from "../../components/molecules/StepsProgressLoader/StepsProgressLoader.types";

export const CSV_CONNECTOR_KEY = "csv";
/** Always sent for a `.csv`: some systems report it as another type, and the signed `PUT` must match. */
export const CSV_CONTENT_TYPE = "text/csv";
/** How long the model may build before the page stops waiting. */
export const MODEL_WAIT_LIMIT_MS = 15 * 60 * 1000;
export const FAST_POLL_MS = 5000;
export const SLOW_POLL_MS = 10000;
export const SLOW_POLL_AFTER_MS = 60 * 1000;

export const findCsvTypeId = (types: Pick<ConnectorTypeRead, "id" | "key">[]): string | null =>
  types.find((type) => type.key === CSV_CONNECTOR_KEY)?.id ?? null;

export const fileStem = (fileName: string): string => fileName.replace(/\.[^./\\]+$/, "") || fileName;

export const isCsvFile = (file: Pick<File, "name">): boolean => /\.csv$/i.test(file.name);

/** The stem, or `"{stem} (2)"`, `(3)`… when a connector already has that name. */
export const uniqueConnectorName = (stem: string, existing: string[]): string => {
  const taken = new Set(existing.map((name) => name.toLowerCase()));
  if (!taken.has(stem.toLowerCase())) return stem;
  let n = 2;
  while (taken.has(`${stem} (${n})`.toLowerCase())) n += 1;
  return `${stem} (${n})`;
};

/** The prompt when the user typed none: names the dataset, so the Story starts from the file. */
export const defaultFilePrompt = (dataset: string): string => `Tell the story of ${dataset}`;

export type FileStoryStepId = "upload" | "read" | "model" | "story";
export type FileStoryProblem =
  | "unreadable"
  | "uploadHeld"
  | "modelHeld"
  | "modelFailed"
  | "timedOut"
  | null;

export interface FileStoryProgressStep {
  id: FileStoryStepId;
  status: StepsProgressStatus;
}

export interface FileStoryProgressView {
  steps: FileStoryProgressStep[];
  problem: FileStoryProblem;
  /** Everything before the Story is done: create it now. */
  readyForStory: boolean;
}

const IDS: FileStoryStepId[] = ["upload", "read", "model", "story"];

const at = (current: number, error: boolean): FileStoryProgressStep[] =>
  IDS.map((id, index) => ({
    id,
    status: index < current ? "completed" : index === current ? (error ? "error" : "inProgress") : "pending",
  }));

/** Maps the upload and its model state to the four steps and, when it stops, why. */
export const fileStoryProgress = (
  upload: UploadRead | undefined,
  model: UploadModelRead | undefined,
  waitedMs = 0,
): FileStoryProgressView => {
  const view = (current: number, problem: FileStoryProblem = null): FileStoryProgressView => ({
    steps: at(current, problem !== null),
    problem,
    readyForStory: false,
  });
  if (!upload) return view(1);
  if (upload.status === "failed") return view(1, "unreadable");
  if (upload.status === "held") return view(1, "uploadHeld");
  if (upload.status !== "loaded") return view(1);
  if (!model) return view(2);
  if (model.state === "held") return view(2, "modelHeld");
  if (model.state === "failed" || model.state === "nothing" || model.state === "off") return view(2, "modelFailed");
  if (model.state === "ready") return { steps: at(3, false), problem: null, readyForStory: true };
  if (waitedMs > MODEL_WAIT_LIMIT_MS) return view(2, "timedOut");
  return view(2);
};

/** The model won't build, but the user may still make a Story on the whole model. */
export const canCreateAnyway = (problem: FileStoryProblem): boolean => problem === "modelFailed";

export const pollInterval = (waitedMs: number): number => (waitedMs < SLOW_POLL_AFTER_MS ? FAST_POLL_MS : SLOW_POLL_MS);
